import type { DeviceStatus } from '../../../../types/DeviceListTypes';
import type { EventDeviceRow } from '../types';
import {
  getDeviceValidationMismatchReasons,
  isDeviceSubjectToFleetValidation,
  isRaceConfigurationReadyForDeviceVerification,
  RACE_CONFIGURATION_REQUIRED_FOR_VERIFICATION_MESSAGE,
  type RaceConfigurationRequirements,
} from '../../../helpers/deviceValidation';
import { pairedFrontIdFromRear } from '../../../helpers/devicePairing';
import { formatApiResponseError } from './eventsOverviewHelpers';
import type {
  PackagingToDistributionReadinessCheck,
  PackagingRaceReadinessPayload,
} from '../types/EventsOverviewTypes';

export type { PackagingToDistributionReadinessCheck, PackagingRaceReadinessPayload };

export function countDistinctAssignedAthletes(rows: EventDeviceRow[]): number {
  const seen = new Set<string>();
  for (const row of rows) {
    const a = String(row.assignedAthleteId ?? '').trim();
    if (a) seen.add(a);
  }
  return seen.size;
}

export function countDevicesAssignedToAthletes(rows: EventDeviceRow[]): number {
  return rows.filter((row) => String(row.assignedAthleteId ?? '').trim()).length;
}

/** Maps `queryEventDeviceStatuses` response rows to deviceId → latest status row. */
export function mapStatusRowsToDeviceStatusById(statuses: unknown[] | undefined): Record<string, DeviceStatus> {
  const statusByDeviceId: Record<string, DeviceStatus> = {};
  for (const raw of statuses ?? []) {
    if (!raw || typeof raw !== 'object') continue;
    const r = raw as Record<string, unknown>;
    const did = typeof r.deviceId === 'string' ? r.deviceId : '';
    if (!did) continue;
    statusByDeviceId[did] = r as DeviceStatus;
  }
  return statusByDeviceId;
}

/** Maps `getDeviceLifecycle` device rows to deviceId → latest lifecycle row (flags + reason). */
export function mapLifecycleRowsByDeviceId(devices: unknown[] | undefined): Record<string, Record<string, unknown>> {
  const lifecycleByDeviceId: Record<string, Record<string, unknown>> = {};
  for (const raw of devices ?? []) {
    if (!raw || typeof raw !== 'object') continue;
    const row = raw as Record<string, unknown>;
    const did = String(row.deviceId ?? '').trim();
    if (did) lifecycleByDeviceId[did] = row;
  }
  return lifecycleByDeviceId;
}

export function parseRaceConfigurationFromApiResponse(raceRes: unknown): {
  cfg: RaceConfigurationRequirements | null;
  loadOk: boolean;
} {
  const rr = raceRes as { ok?: boolean; data?: Record<string, unknown> };
  if (!rr?.ok) {
    return { cfg: null, loadOk: false };
  }
  const data = (rr?.data ?? {}) as Record<string, unknown>;
  const c = (data.configuration ?? {}) as Record<string, unknown>;
  const cfg: RaceConfigurationRequirements = {
    fw_version: typeof c.fw_version === 'string' ? c.fw_version.trim() : '',
    fw_version_front: typeof c.fw_version_front === 'string' ? c.fw_version_front.trim() : '',
    min_battery: Number.isFinite(Number(c.min_battery)) ? Number(c.min_battery) : undefined,
    max_debug_size: Number.isFinite(Number(c.max_debug_size)) ? Number(c.max_debug_size) : undefined,
    max_race_size: Number.isFinite(Number(c.max_race_size)) ? Number(c.max_race_size) : undefined,
  };
  return { cfg, loadOk: true };
}

export function computePackagingToDistributionReadinessChecks(input: {
  displayRows: EventDeviceRow[];
  raceConfig: RaceConfigurationRequirements | null;
  raceConfigLoadOk: boolean;
  statusByDeviceId: Record<string, DeviceStatus>;
  lifecycleByDeviceId: Record<string, unknown>;
  nowMs?: number;
}): PackagingToDistributionReadinessCheck[] {
  const nowMs = input.nowMs ?? Date.now();
  const deviceRows = input.displayRows.filter((row) => String(row.deviceId ?? '').trim());
  const deviceCount = deviceRows.length;
  const assignedToAthleteCount = countDevicesAssignedToAthletes(deviceRows);
  const cfg = input.raceConfig;
  const hasFw = !!(cfg?.fw_version && String(cfg.fw_version).trim());
  const hasFwFront = !!(cfg?.fw_version_front && String(cfg.fw_version_front).trim());
  const raceConfigUploaded = isRaceConfigurationReadyForDeviceVerification(cfg, input.raceConfigLoadOk);

  const checks: PackagingToDistributionReadinessCheck[] = [
    {
      key: 'devicesAssignedToAthletes',
      label: 'One or more devices assigned to athletes',
      ok: assignedToAthleteCount >= 1,
      detail:
        assignedToAthleteCount >= 1
          ? undefined
          : 'Assign at least one device to an athlete on this race.',
    },
    {
      key: 'raceConfigurationUploaded',
      label: 'Race configuration file uploaded',
      ok: raceConfigUploaded,
      detail: !input.raceConfigLoadOk
        ? 'Race configuration could not be loaded.'
        : !hasFw
          ? RACE_CONFIGURATION_REQUIRED_FOR_VERIFICATION_MESSAGE
          : !hasFwFront
            ? 'Front firmware requirement is not set. Go back and set it via Actions → Edit race configuration.'
            : undefined,
    },
  ];

  const mismatching: string[] = [];
  for (const row of deviceRows) {
    const did = String(row.deviceId ?? '').trim();
    if (!did || !isDeviceSubjectToFleetValidation(did)) continue;
    const st = input.statusByDeviceId[did];
    const life = input.lifecycleByDeviceId[did];
    const pairedFrontId = pairedFrontIdFromRear(did);
    const frontStatus = pairedFrontId ? input.statusByDeviceId[pairedFrontId] : undefined;
    const reasons = getDeviceValidationMismatchReasons(cfg, st, life, nowMs, frontStatus);
    if (reasons.length > 0) mismatching.push(did);
  }

  const rearDeviceCount = deviceRows.filter((row) => isDeviceSubjectToFleetValidation(String(row.deviceId ?? ''))).length;
  const allConformOk = rearDeviceCount > 0 && mismatching.length === 0;
  checks.push({
    key: 'devicesMeetConfiguration',
    label: 'All rear devices meet race configuration and validation checks',
    ok: allConformOk,
    blocking: true,
    state: allConformOk ? 'pass' : 'fail',
    detail: allConformOk
      ? undefined
      : rearDeviceCount === 0
        ? 'No rear devices assigned to this race.'
        : `${mismatching.length} rear device(s) need attention (e.g. ${mismatching.slice(0, 4).join(', ')}${mismatching.length > 4 ? ', …' : ''}). To view the full race validation report click the "Validation Report" button in the actions tab.`,
  });

  return checks;
}

export function isPackagingToDistributionFullyReady(checks: PackagingToDistributionReadinessCheck[]): boolean {
  const blocking = checks.filter((c) => c.blocking !== false);
  return blocking.length > 0 && blocking.every((c) => c.ok);
}

export async function fetchPackagingRacesDistributionReadiness(
  eventId: string,
  packagingRaces: PackagingRaceReadinessPayload[],
): Promise<Record<string, PackagingToDistributionReadinessCheck[] | { error: string }>> {
  const eid = String(eventId ?? '').trim();
  const out: Record<string, PackagingToDistributionReadinessCheck[] | { error: string }> = {};
  if (!eid || packagingRaces.length === 0) return out;

  const api = window.eventsAPI as
    | {
      getRaceConfiguration?: (p: { eventId: string; raceId: string }) => Promise<unknown>;
      queryEventDeviceStatuses?: (eventId: string, deviceIds: string[]) => Promise<unknown>;
      getDeviceLifecycle?: (p: { deviceIds: string[] }) => Promise<unknown>;
    }
    | undefined;

  if (!api?.getRaceConfiguration || !api?.queryEventDeviceStatuses) {
    for (const pr of packagingRaces) {
      out[pr.raceId] = { error: 'Events API is not available' };
    }
    return out;
  }

  try {
    const cfgResults = await Promise.all(
      packagingRaces.map((pr) =>
        api.getRaceConfiguration!({ eventId: eid, raceId: String(pr.raceId).trim() }),
      ),
    );

    const allIds = new Set<string>();
    for (const pr of packagingRaces) {
      for (const row of pr.displayRows) {
        const did = String(row.deviceId ?? '').trim();
        if (did) allIds.add(did);
      }
    }
    const deviceIds = [...allIds].sort();

    let statusByDeviceId: Record<string, DeviceStatus> = {};
    let lifecycleByDeviceId: Record<string, unknown> = {};

    if (deviceIds.length > 0) {
      const [stRes, lifecycleRes] = await Promise.all([
        api.queryEventDeviceStatuses(eid, deviceIds),
        api.getDeviceLifecycle
          ? api.getDeviceLifecycle({ deviceIds })
          : Promise.resolve({ ok: false, data: { devices: [] } }),
      ]);

      const statusRes = stRes as { ok?: boolean; data?: { statuses?: unknown[] } };
      if (!statusRes?.ok) {
        const msg = formatApiResponseError(statusRes, 'Failed to load device statuses');
        for (const pr of packagingRaces) {
          out[pr.raceId] = { error: msg };
        }
        return out;
      }
      const statusRows = Array.isArray(statusRes.data?.statuses) ? statusRes.data!.statuses! : [];
      statusByDeviceId = mapStatusRowsToDeviceStatusById(statusRows);

      const lifecycleRows = Array.isArray((lifecycleRes as { data?: { devices?: unknown[] } })?.data?.devices)
        ? ((lifecycleRes as { data: { devices: unknown[] } }).data.devices as unknown[])
        : [];
      lifecycleByDeviceId = mapLifecycleRowsByDeviceId(lifecycleRows);
    }

    packagingRaces.forEach((pr, i) => {
      const { cfg, loadOk } = parseRaceConfigurationFromApiResponse(cfgResults[i]);
      const raceId = String(pr.raceId).trim();
      out[pr.raceId] = computePackagingToDistributionReadinessChecks({
        displayRows: pr.displayRows,
        raceConfig: cfg,
        raceConfigLoadOk: loadOk,
        statusByDeviceId,
        lifecycleByDeviceId,
        nowMs: Date.now(),
      });
    });
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : 'Failed to load readiness';
    for (const pr of packagingRaces) {
      if (out[pr.raceId] == null) out[pr.raceId] = { error: msg };
    }
  }
  return out;
}
